import { formatBytes } from '../../../components/company-data-export/exportFormat';

/**
 * Rótulos pt-BR das ações da trilha de auditoria do prontuário (LGPD L-14, ver
 * public/api/patients/PatientRecordAudit.php). Uma ação nova ainda sem rótulo mapeado
 * cai no fallback e mostra o próprio código, mesmo padrão de AUDIT_ACTION_LABELS
 * (Admin Geral > Auditoria).
 */
export const AUDIT_ACTION_LABELS = {
  'patient.record.read': 'Abriu o prontuário',
  'patient.documents.read': 'Consultou os documentos',
  'patient.attachment.download': 'Abriu um anexo',
  'patient.anamnese.create': 'Registrou anamnese',
  'patient.anamnese.update': 'Alterou anamnese',
  'patient.anamnese.delete': 'Excluiu anamnese',
  'patient.evolution.create': 'Registrou evolução',
  'patient.evolution.annul': 'Anulou evolução',
  'patient.evolution.replace': 'Substituiu evolução',
  'patient.complaint.update': 'Atualizou as queixas',
  'patient.note.create': 'Registrou anotação',
  'patient.note.update': 'Alterou anotação',
  'patient.cranial.create': 'Registrou medição craniana',
  'patient.cranial.update': 'Alterou medição craniana',
  'patient.cranial.delete': 'Excluiu medição craniana',
  'patient.body_measurements.create': 'Registrou medidas corporais',
  'patient.body_measurements.update': 'Alterou medidas corporais',
  'patient.body_measurements.delete': 'Excluiu medidas corporais',
  'patient.body_map.create': 'Registrou mapa corporal',
  'patient.body_map.update': 'Alterou mapa corporal',
  'patient.body_map.delete': 'Excluiu mapa corporal',
  'patient.meal_plan.create': 'Registrou plano alimentar',
  'patient.meal_plan.update': 'Alterou plano alimentar',
  'patient.meal_plan.delete': 'Excluiu plano alimentar',
  'patient.application_map.create': 'Registrou mapa de aplicação',
  'patient.application_map.update': 'Alterou mapa de aplicação',
  'patient.application_map.delete': 'Excluiu mapa de aplicação',
  'patient.scale_assessment.create': 'Registrou escala clínica',
  'patient.scale_assessment.update': 'Alterou escala clínica',
  'patient.scale_assessment.delete': 'Excluiu escala clínica',
  'patient.growth_measurement.create': 'Registrou medição de crescimento',
  'patient.growth_measurement.update': 'Alterou medição de crescimento',
  'patient.growth_measurement.delete': 'Excluiu medição de crescimento',
  'patient.motor_milestone.create': 'Registrou marco motor',
  'patient.motor_milestone.update': 'Alterou marco motor',
  'patient.motor_milestone.delete': 'Excluiu marco motor',
  'patient.thermography.create': 'Registrou termografia',
  'patient.thermography.update': 'Alterou termografia',
  'patient.thermography.delete': 'Excluiu termografia',
  'patient.aims.create': 'Registrou escala de Alberta',
  'patient.aims.update': 'Alterou escala de Alberta',
  'patient.aims.delete': 'Excluiu escala de Alberta',
  'patient.prescription.create': 'Emitiu receita',
  'patient.prescription.update': 'Alterou receita',
  'patient.prescription.delete': 'Excluiu receita',
  'patient.diagnosis.create': 'Registrou diagnóstico (CID)',
  'patient.diagnosis.delete': 'Excluiu diagnóstico (CID)',
  'patient.birth_info.update': 'Alterou idade gestacional',
  'patient.attachment.create': 'Enviou anexo',
  'patient.attachment.update': 'Alterou anexo',
  'patient.attachment.delete': 'Excluiu anexo',
  'patient.document.create': 'Gerou documento',
  'patient.create': 'Cadastrou o paciente',
  'patient.update': 'Alterou dados cadastrais',
  'patient.appointment.delete': 'Excluiu agendamento',
  'patient.export': 'Exportou os dados',
  'patients.list_export': 'Exportou a lista de pessoas atendidas',
  'patient.anonymize': 'Anonimizou o paciente',
  'patient.delete': 'Excluiu o paciente',
  'patient.whatsapp_opt_out': 'Parou mensagens de relacionamento',
  'patient.whatsapp_opt_out_revert': 'Voltou a enviar mensagens de relacionamento',
};

/**
 * Rótulo pt-BR de uma ação da trilha, com um caso especial: a leitura do prontuário feita
 * a partir da tela de consulta (details.via === 'appointment') ganha um texto mais específico.
 * @param {string} action
 * @param {Object|null} [details]
 * @returns {string}
 */
export function getAuditActionLabel(action, details) {
  if (action === 'patient.record.read' && details && details.via === 'appointment') {
    return 'Abriu o prontuário na consulta';
  }
  return AUDIT_ACTION_LABELS[action] || action || '—';
}

/**
 * Resumo curto em pt-BR do JSON de `details` de uma entrada, priorizando os campos mais
 * comuns registrados pela trilha (ids de referência, contexto, contagens da exportação).
 * @param {Object|null} details
 * @returns {string} vazio quando não há nada relevante para mostrar
 */
export function getAuditDetailsSummary(details) {
  if (!details || typeof details !== 'object') return '';

  const parts = [];

  if (details.appointmentId) parts.push(`consulta #${details.appointmentId}`);
  if (details.attachmentId) parts.push(`anexo #${details.attachmentId}`);
  if (details.anamneseId) parts.push(`anamnese #${details.anamneseId}`);
  if (details.recordId) parts.push(`registro #${details.recordId}`);
  if (details.noteId) parts.push(`anotação #${details.noteId}`);
  if (details.measurementId) parts.push(`medição #${details.measurementId}`);
  if (details.assessmentId) parts.push(`avaliação #${details.assessmentId}`);
  if (details.mapId) parts.push(`mapa #${details.mapId}`);
  if (details.documentId) parts.push(`documento #${details.documentId}`);
  if (details.context) parts.push(String(details.context));
  if (Array.isArray(details.tables) && details.tables.length) parts.push(`${details.tables.length} tabela(s)`);
  if (Array.isArray(details.files) && details.files.length) parts.push(`${details.files.length} arquivo(s)`);
  if (details.bytes) parts.push(formatBytes(details.bytes));
  // Exportação da lista inteira (patients.list_export): formato e quantas pessoas
  if (details.format) parts.push(details.format === 'excel' ? 'Excel' : String(details.format).toUpperCase());
  if (Number.isFinite(details.count)) parts.push(`${details.count} ${details.count === 1 ? 'pessoa' : 'pessoas'}`);

  return parts.join(' · ');
}
